import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Digite seu email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email enviado com sucesso!');
    } catch (error) {
      // Sempre mostra sucesso para não revelar se o email existe
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
              <span className="text-white font-bold text-xl">R</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">RPB SISTEMAS</h1>
              <p className="text-slate-500 text-sm">INTELIGENTES</p>
            </div>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-2xl">
                {sent ? 'Email Enviado!' : 'Esqueceu sua senha?'}
              </CardTitle>
              <CardDescription>
                {sent 
                  ? 'Verifique sua caixa de entrada e siga as instruções do email.' 
                  : 'Digite seu email e enviaremos um link para redefinir sua senha.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {sent ? (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-emerald-600" />
                    </div>
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-slate-600">
                      Se o email <span className="font-medium text-slate-900">{email}</span> estiver cadastrado, 
                      você receberá um link para redefinir sua senha.
                    </p>
                    <p className="text-sm text-slate-500">
                      O link expira em 1 hora.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full h-11"
                      onClick={() => setSent(false)}
                    >
                      <Mail size={18} className="mr-2" />
                      Enviar para outro email
                    </Button>
                    <Link to="/login" className="block">
                      <Button variant="ghost" className="w-full h-11">
                        <ArrowLeft size={18} className="mr-2" />
                        Voltar ao login
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email"
                      className="h-11"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="forgot-submit"
                  >
                    {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Mail size={18} className="mr-2" />}
                    Enviar link de recuperação
                  </Button>
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full h-11">
                      <ArrowLeft size={18} className="mr-2" />
                      Voltar ao login
                    </Button>
                  </Link>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
        <div className="relative z-10 text-center">
          <div className="w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-blue-600/30">
            <span className="text-white font-bold text-3xl">R</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">RPB SISTEMAS INTELIGENTES</h2>
          <p className="text-slate-400 text-lg max-w-md">
            Recupere o acesso à sua conta de forma rápida e segura.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
